<template>
    <div>
        <p>
            Group Applications by source node and Docker container name. Patterns support <var>*</var> and <var>?</var>. The first group matching all
            instances is used; unmatched Applications appear under Other applications. Changes are saved separately and preserved across deployments.
        </p>
        <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
        <v-simple-table>
            <thead>
                <tr>
                    <th>Group</th>
                    <th>Source nodes</th>
                    <th>Container</th>
                    <th>Expanded by default</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(group, index) in groups" :key="group.id">
                    <td>{{ group.name }}</td>
                    <td>{{ group.node_patterns.join(', ') }}</td>
                    <td>{{ group.container_patterns.join(', ') }}</td>
                    <td>{{ group.expanded ? 'Yes' : 'No' }}</td>
                    <td class="text-no-wrap">
                        <v-btn icon small :disabled="saving || index === 0" title="Move up" @click="move(index, -1)"
                            ><v-icon small>mdi-arrow-up</v-icon></v-btn
                        >
                        <v-btn icon small :disabled="saving || index === groups.length - 1" title="Move down" @click="move(index, 1)"
                            ><v-icon small>mdi-arrow-down</v-icon></v-btn
                        >
                        <v-btn icon small :disabled="saving" title="Edit group" @click="edit(group)"><v-icon small>mdi-pencil</v-icon></v-btn>
                        <v-btn icon small :disabled="saving" title="Remove group" @click="remove(group)"><v-icon small>mdi-delete</v-icon></v-btn>
                    </td>
                </tr>
            </tbody>
        </v-simple-table>
        <v-btn small color="primary" class="mt-3" :disabled="!editable || saving" @click="edit()">Add group</v-btn>
        <v-dialog v-model="dialog" max-width="700">
            <v-card class="pa-5">
                <v-card-title>{{ originalID ? 'Edit group' : 'Add group' }}</v-card-title>
                <v-text-field v-model="form.name" label="Group name" maxlength="150" outlined dense />
                <v-combobox
                    v-model="form.node_patterns"
                    :items="nodes"
                    item-text="text"
                    item-value="value"
                    :return-object="false"
                    multiple
                    chips
                    small-chips
                    label="Source nodes"
                    hint="Select a hostname or enter a pattern, e.g. Mac-mini-01-Docker."
                    persistent-hint
                    outlined
                    dense
                />
                <v-textarea
                    v-model="containerPatterns"
                    label="Container names (one pattern per line)"
                    hint="For example, api-portal-redis or n8n*. Omit the /docker/ prefix."
                    persistent-hint
                    rows="3"
                    outlined
                    class="mt-3"
                />
                <v-checkbox v-model="form.expanded" label="Expand by default" />
                <v-alert v-if="formError" type="error" dense text>{{ formError }}</v-alert>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="saving" @click="dialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';

export default {
    data() {
        return {
            groups: [],
            nodes: [],
            error: '',
            formError: '',
            editable: false,
            dialog: false,
            originalID: '',
            saving: false,
            containerPatterns: '',
            form: {},
        };
    },
    mounted() {
        this.load();
    },
    watch: {
        '$route.params.projectId'() {
            this.dialog = false;
            this.saving = false;
            this.load();
        },
    },
    methods: {
        load() {
            const projectId = this.$route.params.projectId;
            this.groups = [];
            this.nodes = [];
            this.editable = false;
            this.$api.getApplicationGroups((data, error) => {
                if (projectId !== this.$route.params.projectId) return;
                this.error = error || '';
                this.editable = !error;
                if (!error) this.groups = data.groups || [];
            });
            this.$api.getOverview('nodes', '', (data, error) => {
                if (projectId !== this.$route.params.projectId) return;
                if (!error) this.nodes = data.nodes.map((node) => ({ text: `${node.display_name || node.name} (${node.name})`, value: node.name }));
            });
        },
        edit(group) {
            this.originalID = group ? group.id : '';
            this.form = group
                ? JSON.parse(JSON.stringify(group))
                : { id: uuidv4(), name: '', node_patterns: [], container_patterns: [], expanded: true };
            this.containerPatterns = this.form.container_patterns.join('\n');
            this.formError = '';
            this.dialog = true;
        },
        persist(groups, done) {
            const projectId = this.$route.params.projectId;
            this.saving = true;
            this.error = '';
            this.$api.saveApplicationGroups(groups, (data, error) => {
                if (projectId !== this.$route.params.projectId) return;
                this.saving = false;
                if (error) {
                    this.error = error;
                    this.formError = error;
                    return;
                }
                this.groups = data.groups || [];
                if (done) done();
            });
        },
        save() {
            const group = {
                ...this.form,
                name: this.form.name.trim(),
                id: this.form.id,
                node_patterns: this.form.node_patterns.map((p) => (typeof p === 'string' ? p.trim() : p.value)).filter(Boolean),
                container_patterns: this.containerPatterns
                    .split('\n')
                    .map((p) => p.trim())
                    .filter(Boolean),
            };
            if (!group.name || !group.id || !group.node_patterns.length || !group.container_patterns.length) {
                this.formError = 'Enter a group name, source nodes, and container patterns.';
                return;
            }
            if (!this.originalID && this.groups.some((g) => g.id === group.id)) {
                this.formError = 'This group already exists.';
                return;
            }
            const groups = this.originalID ? this.groups.map((g) => (g.id === this.originalID ? group : g)) : [...this.groups, group];
            this.persist(groups, () => {
                this.dialog = false;
            });
        },
        move(index, direction) {
            const groups = this.groups.slice();
            [groups[index], groups[index + direction]] = [groups[index + direction], groups[index]];
            this.persist(groups);
        },
        remove(group) {
            this.persist(this.groups.filter((g) => g.id !== group.id));
        },
    },
};
</script>
